/* eslint-disable react/no-array-index-key */
/* eslint-disable no-return-assign */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { format } from "date-fns";
import { MdClose, MdVideoCall } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import { CalendarEvent, NewEventPayload } from "../types";
import { useSearchPeople } from "../hooks/useCalendarEvents";
import * as S from "../styles";

interface NewEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (event: NewEventPayload) => void;
  editingEvent?: CalendarEvent | null;
  defaultDate?: Date;
  defaultHour?: number;
  loading?: boolean;
}

const getTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/Sao_Paulo";
  }
};

const NewEventModal: React.FC<NewEventModalProps> = ({
  open,
  onClose,
  onSave,
  editingEvent,
  defaultDate,
  defaultHour,
  loading,
}) => {
  const [subject, setSubject] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [isOnlineMeeting, setIsOnlineMeeting] = useState(false);
  const [isAllDay, setIsAllDay] = useState(false);
  const [attendeeEmail, setAttendeeEmail] = useState("");
  const [attendees, setAttendees] = useState<
    Array<{ address: string; name?: string }>
  >([]);
  const [peopleQuery, setPeopleQuery] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputParticipantesRef = useRef<HTMLInputElement>(null);

  const { data: searchResults, isLoading: searchLoading } =
    useSearchPeople(peopleQuery);

  const updateDropdownPos = useCallback(() => {
    const rect = inputParticipantesRef.current?.getBoundingClientRect();
    if (rect) {
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Preencher com valores default ou do evento editado
  useEffect(() => {
    if (editingEvent) {
      setSubject(editingEvent.subject || "");
      setIsAllDay(editingEvent.isAllDay || false);
      setIsOnlineMeeting(editingEvent.isOnlineMeeting || false);
      setLocation(editingEvent.location?.displayName || "");
      setDescription(editingEvent.bodyPreview || "");

      if (editingEvent.start?.dateTime) {
        const start = new Date(editingEvent.start.dateTime);
        setDate(format(start, "yyyy-MM-dd"));
        setStartTime(format(start, "HH:mm"));
      }
      if (editingEvent.end?.dateTime) {
        const end = new Date(editingEvent.end.dateTime);
        setEndTime(format(end, "HH:mm"));
      }
      if (editingEvent.attendees) {
        setAttendees(
          editingEvent.attendees
            .filter((a) => a.emailAddress?.address)
            .map((a) => ({
              address: a.emailAddress!.address!,
              name: a.emailAddress?.name,
            }))
        );
      }
    } else {
      setSubject("");
      setLocation("");
      setDescription("");
      setIsOnlineMeeting(false);
      setIsAllDay(false);
      setAttendees([]);

      if (defaultDate) {
        setDate(format(defaultDate, "yyyy-MM-dd"));
      } else {
        setDate(format(new Date(), "yyyy-MM-dd"));
      }
      if (defaultHour !== undefined) {
        setStartTime(`${defaultHour.toString().padStart(2, "0")}:00`);
        setEndTime(`${(defaultHour + 1).toString().padStart(2, "0")}:00`);
      } else {
        setStartTime("09:00");
        setEndTime("10:00");
      }
    }
  }, [editingEvent, defaultDate, defaultHour, open]);

  const handleAddAttendee = useCallback(() => {
    if (
      attendeeEmail &&
      attendeeEmail.includes("@") &&
      !attendees.find((a) => a.address === attendeeEmail)
    ) {
      setAttendees((prev) => [...prev, { address: attendeeEmail }]);
      setAttendeeEmail("");
      setPeopleQuery("");
    }
  }, [attendeeEmail, attendees]);

  const handleSelectPerson = (address: string, name?: string) => {
    if (!attendees.find((a) => a.address === address)) {
      setAttendees((prev) => [...prev, { address, name }]);
    }
    setPeopleQuery("");
    setAttendeeEmail("");
  };

  const handleRemoveAttendee = (address: string) => {
    setAttendees((prev) => prev.filter((a) => a.address !== address));
  };

  const handleSubmit = () => {
    if (!subject.trim() || !date) return;

    const payload: NewEventPayload = {
      subject: subject.trim(),
      start: {
        dateTime: isAllDay ? `${date}T00:00:00` : `${date}T${startTime}:00`,
        timeZone: getTimeZone(),
      },
      end: {
        dateTime: isAllDay ? `${date}T23:59:59` : `${date}T${endTime}:00`,
        timeZone: getTimeZone(),
      },
      isAllDay,
      isOnlineMeeting,
      onlineMeetingProvider: isOnlineMeeting ? "teamsForBusiness" : undefined,
    };

    if (location.trim()) {
      payload.location = { displayName: location.trim() };
    }
    if (description.trim()) {
      payload.body = { contentType: "text", content: description.trim() };
    }
    if (attendees.length > 0) {
      payload.attendees = attendees.map((a) => ({
        emailAddress: { address: a.address, name: a.name },
        type: "required",
      }));
    }

    onSave(payload);
  };

  const showDropdown = peopleQuery.length >= 2;

  if (!open) return null;

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.ModalContent onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle>
            {editingEvent ? "Editar evento" : "Novo evento"}
          </S.ModalTitle>
          <S.ModalCloseButton onClick={onClose}>
            <MdClose size={20} />
          </S.ModalCloseButton>
        </S.ModalHeader>

        <S.ModalBody>
          <S.FormGroup>
            <S.FormLabel>Titulo *</S.FormLabel>
            <S.FormInput
              type="text"
              placeholder="Adicionar um título"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              autoFocus
            />
          </S.FormGroup>

          <S.FormGroup>
            <S.FormLabel>Data</S.FormLabel>
            <S.FormInput
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </S.FormGroup>

          {!isAllDay && (
            <S.FormRow>
              <S.FormGroup>
                <S.FormLabel>Inicio</S.FormLabel>
                <S.FormInput
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </S.FormGroup>
              <S.FormGroup>
                <S.FormLabel>Fim</S.FormLabel>
                <S.FormInput
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </S.FormGroup>
            </S.FormRow>
          )}

          <S.CheckboxRow onClick={() => setIsAllDay(!isAllDay)}>
            <S.CustomCheckbox $checked={isAllDay} />
            Dia inteiro
          </S.CheckboxRow>

          <S.FormGroup>
            <S.FormLabel>Local</S.FormLabel>
            <S.FormInput
              type="text"
              placeholder="Adicionar local"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </S.FormGroup>

          <S.CheckboxRow onClick={() => setIsOnlineMeeting(!isOnlineMeeting)}>
            <S.ToggleSwitch $checked={isOnlineMeeting} />
            <MdVideoCall
              size={20}
              style={{
                color: isOnlineMeeting ? "#5C6BC0" : "#9e9e9e",
                transition: "color 0.2s",
              }}
            />
            Criar reunião do Teams
          </S.CheckboxRow>

          {/* Participantes */}
          <S.FormGroup>
            <S.FormLabel>Participantes</S.FormLabel>
            <S.FormInput
              ref={inputParticipantesRef}
              type="text"
              placeholder="Buscar pessoas ou digitar e-mail"
              value={attendeeEmail}
              onChange={(e) => {
                setAttendeeEmail(e.target.value);
                setPeopleQuery(e.target.value);
                updateDropdownPos();
              }}
              onFocus={updateDropdownPos}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddAttendee();
                }
              }}
            />

            {/* Lista de participantes adicionados */}
            {attendees.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  marginTop: 8,
                }}
              >
                {attendees.map((att) => (
                  <div
                    key={att.address}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 8px",
                      background: "#e8eaf6",
                      borderRadius: 16,
                      fontSize: 12,
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    {att.name || att.address}
                    <MdClose
                      size={14}
                      style={{ cursor: "pointer", color: "#5f6368" }}
                      onClick={() => handleRemoveAttendee(att.address)}
                    />
                  </div>
                ))}
              </div>
            )}
          </S.FormGroup>

          <S.FormGroup>
            <S.FormLabel>Descrição</S.FormLabel>
            <S.FormTextarea
              placeholder="Adicionar descrição"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </S.FormGroup>
        </S.ModalBody>

        <S.ModalFooter>
          <S.ModalButton onClick={onClose}>Cancelar</S.ModalButton>
          <S.ModalButton
            $variant="primary"
            onClick={handleSubmit}
            disabled={!subject.trim() || !date || loading}
          >
            {loading ? (
              <CircularProgress size={16} style={{ color: "#fff" }} />
            ) : editingEvent ? (
              "Salvar"
            ) : (
              "Criar"
            )}
          </S.ModalButton>
        </S.ModalFooter>
      </S.ModalContent>

      {/* Dropdown de busca - renderizado fora da modal para não causar scroll */}
      {showDropdown && (
        <div
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width || "auto",
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 8,
            maxHeight: 240,
            overflowY: "auto",
            zIndex: 1400,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            padding: "4px 0",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {searchLoading ? (
            <div
              style={{
                padding: 16,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={20} />
            </div>
          ) : searchResults && searchResults.length > 0 ? (
            searchResults.map((person, idx) => {
              const email = person.scoredEmailAddresses?.[0]?.address;
              if (!email) return null;
              const initials = (person.displayName || email)
                .split(" ")
                .map((w: string) => w[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();
              return (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    cursor: "pointer",
                    transition: "background 0.15s",
                    borderRadius: 6,
                    margin: "0 4px",
                  }}
                  onClick={() =>
                    handleSelectPerson(email, person.displayName || undefined)
                  }
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f0f2ff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "#5C6BC0",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: "Inter, sans-serif",
                      flexShrink: 0,
                    }}
                  >
                    {initials}
                  </div>
                  <div style={{ overflow: "hidden" }}>
                    <div
                      style={{
                        fontWeight: 500,
                        fontSize: 14,
                        color: "#202124",
                        fontFamily: "Inter, sans-serif",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {person.displayName}
                    </div>
                    <div
                      style={{
                        color: "#70757a",
                        fontSize: 12,
                        fontFamily: "Inter, sans-serif",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {email}
                      {person.jobTitle && (
                        <span style={{ color: "#9e9e9e" }}>
                          {" "}
                          · {person.jobTitle}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div
              style={{
                padding: 16,
                color: "#70757a",
                fontSize: 13,
                textAlign: "center",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Nenhum resultado. Pressione Enter para adicionar o e-mail.
            </div>
          )}
        </div>
      )}
    </S.ModalOverlay>
  );
};

export default NewEventModal;
